import { useEffect, useRef, useState } from "react";
import { Camera, ImageIcon, RotateCcw, Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PhotoCapture({ onDownloadInitiated, onSaveAsset, saving }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);
  const [preview, setPreview] = useState(null); // { dataUrl, blob, width, height, filename }

  useEffect(() => {
    const ok = typeof navigator !== "undefined" && navigator.mediaDevices;
    setSupported(!!ok);
  }, []);

  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  const enable = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setError(null);
    } catch (e) {
      const name = e?.name || "";
      setError(
        name === "NotAllowedError"
          ? "Camera permission was denied."
          : name === "NotFoundError"
          ? "No camera was found on this device."
          : "Could not access the camera."
      );
    }
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob(
      (blob) => {
        if (!blob) return;
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const filename = `photo-${ts}.png`;
        const dataUrl = c.toDataURL("image/png");
        setPreview({ dataUrl, blob, width: w, height: h, filename });
      },
      "image/png",
      0.95
    );
  };

  const retake = () => setPreview(null);

  const download = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview.dataUrl;
    a.download = preview.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onDownloadInitiated?.({ filename: preview.filename, fileType: "image/png" });
  };

  const save = () => {
    if (!preview) return;
    onSaveAsset?.({
      filename: preview.filename,
      mimeType: "image/png",
      sizeBytes: preview.blob.size,
      width: preview.width,
      height: preview.height,
      dataUrl: preview.dataUrl,
      blob: preview.blob,
    });
  };

  if (!supported) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6" data-testid="photo-unsupported">
        <p className="text-sm text-destructive">Your browser does not support camera capture.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 sm:p-6 shadow-sm min-w-0" data-testid="photo-capture">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2.5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Photo Capture</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Captured photos live in this browser tab until saved or downloaded.
          </p>
        </div>
        {!streamRef.current && (
          <Button size="sm" onClick={enable} data-testid="photo-request-permission" className="h-8 text-xs">
            <Camera className="mr-1.5 h-3.5 w-3.5" /> Enable Camera
          </Button>
        )}
      </div>
      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs sm:text-sm text-destructive" data-testid="photo-permission-error">
          {error}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Live preview</p>
          <video
            ref={videoRef}
            className="w-full rounded-md border border-border bg-black"
            playsInline
            muted
            autoPlay
            data-testid="photo-live-preview"
          />
          <div className="mt-3">
            <Button
              onClick={capture}
              disabled={!streamRef.current}
              data-testid="photo-capture-button"
              className="bg-accent text-accent-foreground hover:bg-accent-hover"
            >
              <ImageIcon className="mr-2 h-4 w-4" /> Capture Photo
            </Button>
          </div>
        </div>
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Captured photo</p>
          {preview ? (
            <>
              <img
                src={preview.dataUrl}
                alt="Captured preview"
                className="w-full rounded-md border border-border"
                data-testid="photo-captured-preview"
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={retake} data-testid="photo-retake">
                  <RotateCcw className="mr-2 h-4 w-4" /> Retake
                </Button>
                <Button variant="outline" onClick={download} data-testid="photo-download">
                  <Download className="mr-2 h-4 w-4" /> Download
                </Button>
                <Button onClick={save} disabled={saving} data-testid="photo-save-asset">
                  <Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save asset"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {preview.width}×{preview.height}
                </span>
              </div>
            </>
          ) : (
            <div className="flex h-full min-h-[180px] items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
              No photo captured yet
            </div>
          )}
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

export default PhotoCapture;
